// ./src/components/generic/ConfirmModal/ConfirmModal.tsx
import React from "react";
import { Modal, Text, Group, Stack } from "@mantine/core";
import { AppButton } from "../AppButton";
import { AppIcon } from "../AppIcon";

export interface ConfirmModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Function called when user confirms */
  onConfirm: () => void;

  /** Modal title */
  title: string;

  /** Main confirmation message */
  message: string;

  /** Optional additional details or warning text */
  details?: string;

  /** Confirm button text */
  confirmLabel?: string;

  /** Cancel button text */
  cancelLabel?: string;

  /** Type of confirmation - affects icon and button color */
  type?: "danger" | "warning" | "info";

  /** Loading state for confirm action */
  confirmInProgress?: boolean;

  /** Icon to show in the modal */
  iconName?: string;

  /** Size of the modal */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  opened,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmLabel,
  cancelLabel = "Cancel",
  type = "danger",
  confirmInProgress = false,
  iconName,
  size = "md",
}) => {
  // Get default props based on type
  const getTypeDefaults = () => {
    switch (type) {
      case "danger":
        return {
          defaultConfirmLabel: "Delete",
          defaultIcon: "AlertTriangle",
          confirmVariant: "danger" as const,
          iconColor: "red.6",
        };
      case "warning":
        return {
          defaultConfirmLabel: "Continue",
          defaultIcon: "AlertCircle",
          confirmVariant: "primary" as const,
          iconColor: "yellow.6",
        };
      case "info":
        return {
          defaultConfirmLabel: "Confirm",
          defaultIcon: "InfoCircle",
          confirmVariant: "primary" as const,
          iconColor: "blue.6",
        };
      default:
        return {
          defaultConfirmLabel: "Confirm",
          defaultIcon: "AlertTriangle",
          confirmVariant: "danger" as const,
          iconColor: "red.6",
        };
    }
  };

  const typeDefaults = getTypeDefaults();
  const finalConfirmLabel = confirmLabel || typeDefaults.defaultConfirmLabel;
  const finalIconName = iconName || typeDefaults.defaultIcon;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!confirmInProgress) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={title}
      size={size}
      centered
      closeOnClickOutside={!confirmInProgress}
      closeOnEscape={!confirmInProgress}
      withCloseButton={!confirmInProgress}
    >
      <Stack gap="lg">
        {/* Header with icon and title */}
        <Group gap="md" align="flex-start">
          <AppIcon
            iconName={finalIconName}
            size={24}
            color={typeDefaults.iconColor}
          />
          <Stack gap="xs" style={{ flex: 1 }}>
            <Text size="sm" c="dimmed">
              {message}
            </Text>
            {details && (
              <Text size="xs" c="dimmed">
                {details}
              </Text>
            )}
          </Stack>
        </Group>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <AppButton
            variant="secondary"
            onClick={handleCancel}
            disabled={confirmInProgress}
          >
            {cancelLabel}
          </AppButton>
          <AppButton
            variant={typeDefaults.confirmVariant}
            onClick={handleConfirm}
            inProgress={confirmInProgress}
          >
            {finalConfirmLabel}
          </AppButton>
        </Group>
      </Stack>
    </Modal>
  );
};
